import React, { useState, useEffect } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { X, Cookie, Settings } from 'lucide-react';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
  timestamp?: string;
}

export function CookieBanner() {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true
    analytics: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const storedConsent = localStorage.getItem('cookie-consent');
    const hideUntil = localStorage.getItem('cookie-banner-hide-until');
    
    if (!storedConsent) {
      // No consent given yet
      setShow(true);
    } else if (hideUntil && new Date().getTime() < parseInt(hideUntil)) {
      // User dismissed banner temporarily
      setShow(false);
    } else if (storedConsent === 'show-again') {
      // User wants to see the banner again
      setShow(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allPreferences: CookiePreferences = {
      essential: true,
      analytics: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookie-consent', 'all');
    localStorage.setItem('cookie-preferences', JSON.stringify(allPreferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShow(false);
    
    // Enable analytics if accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
  };

  const handleAcceptEssential = () => {
    const essentialPreferences: CookiePreferences = {
      essential: true,
      analytics: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookie-consent', 'essential-only');
    localStorage.setItem('cookie-preferences', JSON.stringify(essentialPreferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShow(false);
    
    // Disable analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleSavePreferences = () => {
    const customPreferences: CookiePreferences = {
      ...preferences,
      essential: true, // Always true
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookie-consent', 'custom');
    localStorage.setItem('cookie-preferences', JSON.stringify(customPreferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShow(false);
    
    // Update analytics based on preference
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied'
      });
    }
  };

  const handleDismiss = () => {
    // Hide banner for 24 hours
    const hideUntil = new Date().getTime() + (24 * 60 * 60 * 1000);
    localStorage.setItem('cookie-banner-hide-until', hideUntil.toString());
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-black/20 backdrop-blur-sm">
      <Card className="max-w-6xl mx-auto p-6 shadow-2xl relative">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Dismiss for now"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="flex flex-col lg:flex-row items-start gap-6">
          {/* Main Content */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <Cookie className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-semibold">Cookie Preferences</h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              We use cookies to enhance your experience on Wisdom Weaver. Some cookies are essential 
              for the platform to function properly, while others help us understand how you use our 
              services and improve them accordingly.
            </p>
            
            {!showDetails && (
              <button
                onClick={() => setShowDetails(true)}
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                <Settings className="h-4 w-4" />
                Manage preferences
              </button>
            )}
          </div>
          
          {/* Actions */}
          {!showDetails ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAcceptEssential}
                className="whitespace-nowrap"
              >
                Essential Only
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
              >
                Accept All
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDetails(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSavePreferences}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Save Preferences
              </Button>
            </div>
          )}
        </div>
        
        {/* Detailed Preferences */}
        {showDetails && (
          <div className="mt-6 pt-6 border-t space-y-4">
            <div className="grid gap-4">
              {/* Essential Cookies */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="essential"
                  checked={true}
                  disabled
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <div className="flex-1">
                  <label htmlFor="essential" className="font-medium text-sm">
                    Essential Cookies
                    <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                      Always Enabled
                    </span>
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Required for the website to function properly. These include authentication, 
                    security, and basic functionality cookies.
                  </p>
                </div>
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="analytics"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <label htmlFor="analytics" className="font-medium text-sm cursor-pointer">
                    Analytics Cookies
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Help us understand how you use the platform so we can improve it. 
                    Data is anonymized and never shared with third parties.
                  </p>
                </div>
              </div>
              
              {/* Functional Cookies */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="functional"
                  checked={preferences.functional}
                  onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <label htmlFor="functional" className="font-medium text-sm cursor-pointer">
                    Functional Cookies
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Enable enhanced functionality and personalization, such as remembering 
                    your preferences and settings across sessions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Footer Links */}
        <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-xs">
          <a 
            href="/legal/privacy-policy" 
            className="text-blue-600 hover:underline"
          >
            Privacy Policy
          </a>
          <a 
            href="/legal/cookie-policy" 
            className="text-blue-600 hover:underline"
          >
            Cookie Policy
          </a>
          <a 
            href="/legal/terms-of-service" 
            className="text-blue-600 hover:underline"
          >
            Terms of Service
          </a>
        </div>
      </Card>
    </div>
  );
}

// Add TypeScript declaration for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'consent',
      action: 'update',
      parameters: { analytics_storage: 'granted' | 'denied' }
    ) => void;
  }
}